import React, { useMemo } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  StyleSheet,
  Text,
  View,
  Pressable,
  ScrollView,
  Image,
  Platform,
} from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { useNavigation } from '@react-navigation/native';

import { GTFonts, useTheme } from '../../theme';
import { s, fs, iconSize, v, borderWidth } from '../../utils/libs';
import { useAppSelector } from '../../redux/helper';
import { isEmployeeOwnerRole } from '../../utils/roles';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';

type Nav = NativeStackNavigationProp<RootStackParamList>;

const BackOfficeIntegrationsScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const navigation = useNavigation<Nav>();
  const isWeb = Platform.OS === 'web';
  // M-Pesa setup is owner-only on the API (managers get 403), so only the
  // owner sees the entry.
  const employeeRole = useAppSelector(
    state => state.auth.inApp.account.employeeInfo?.bp_employee_role,
  );
  const canManageMpesa = isEmployeeOwnerRole(employeeRole);

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={styles.title}>Integrations</Text>
          <Text style={styles.subtitle}>
            Connect external services to automate workflows and payments.
          </Text>
        </View>

        {canManageMpesa ? (
          <Pressable
            style={({ pressed, hovered }: any) => [
              styles.item,
              isWeb && styles.itemWeb,
              isWeb && hovered && styles.itemHovered,
              pressed && { opacity: 0.85 },
            ]}
            onPress={() => navigation.navigate('MpesaIntegration')}>
            <View style={styles.itemLeft}>
              <View style={styles.iconWrap}>
                <Image
                  source={require('../../assets/mpesa.png')}
                  style={styles.mpesaIcon}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.itemTextWrap}>
                <Text style={styles.itemTitle}>M-Pesa</Text>
                <Text style={styles.itemSubtitle}>
                  Connect your Paybill or Till for automated payment
                  confirmation.
                </Text>
              </View>
            </View>
            <Ionicons
              name="chevron-forward"
              size={iconSize(18)}
              color={theme.colors.textSecondary}
            />
          </Pressable>
        ) : null}

        <Pressable style={[styles.item]} disabled>
          <View style={styles.itemLeft}>
            <View style={[styles.iconWrap, styles.whatsappIconWrap]}>
              <MaterialCommunityIcons
                name="whatsapp"
                size={iconSize(20)}
                color={theme.colors.textInverse}
              />
            </View>
            <View style={styles.itemTextWrap}>
              <Text style={styles.itemTitle}>WhatsApp</Text>
              <Text style={styles.itemSubtitle}>
                Coming soon. We’re working on WhatsApp integration.
              </Text>
            </View>
          </View>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>Soon</Text>
          </View>
        </Pressable>
      </ScrollView>
    </View>
  );
};

export default BackOfficeIntegrationsScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    content: {
      paddingHorizontal: s(12),
      paddingVertical: s(12),
      gap: v(12),
    },
    header: {
      marginBottom: v(8),
    },
    title: {
      fontFamily: GTFonts.bold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: theme.colors.text,
    },
    subtitle: {
      marginTop: v(4),
      fontSize: fs(12),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    itemWeb: {
      ...(Platform.OS === 'web'
        ? ({ cursor: 'pointer', transition: 'all 0.15s ease-out' } as any)
        : {}),
    },
    itemHovered: {
      backgroundColor: 'rgba(0,0,0,0.03)',
    },
    item: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s(10),
      paddingVertical: s(10),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      borderWidth: borderWidth(1),
      elevation: 1,
      shadowColor: theme.colors.shadow,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      borderColor: theme.colors.border,
    },
    itemDisabled: {
      opacity: 0.6,
    },
    itemLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginRight: s(6),
    },
    iconWrap: {
      width: s(56),
      height: v(52),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: s(20),
    },
    mpesaIcon: {
      width: s(70),
      height: s(70),
    },
    whatsappIconWrap: {
      backgroundColor: theme.colors.success,
    },
    itemTextWrap: {
      flexShrink: 1,
    },
    itemTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(19),
      color: theme.colors.text,
      marginBottom: v(2),
    },
    itemSubtitle: {
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    badge: {
      paddingHorizontal: s(8),
      paddingVertical: s(2),
      borderRadius: theme.borderRadius.md,
      backgroundColor:
        (theme.colors.borderSecondary ?? theme.colors.border) + '33',
      alignSelf: 'center',
    },
    badgeText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(14),
      color: theme.colors.textSecondary,
    },
  });
